<template>
  <Popover ref="popoverRef" :pt="{ content: '!p-0' }">
    <!-- Single-side mode: full-width row buttons -->
    <div v-if="props.side" class="min-w-[280px] max-w-[400px] p-2 flex flex-col gap-1.5">
      <Button
        v-for="contract in contracts"
        :key="contract.exchange"
        severity="secondary"
        :ib-variant="sideVariant(contract)"
        size="small"
        class="group !relative !w-full !h-auto !px-3 !py-2.5 !justify-between !overflow-visible !tabular-nums !whitespace-nowrap !font-semibold !text-sm"
        @click="props.side === 'yes' ? handleYesClick(contract) : handleNoClick(contract)"
      >
        <div class="flex items-center gap-2.5">
          <ExchangeAvatarGroup :exchange="contract.exchange" size="small" />
          <div class="flex flex-col items-start gap-0.5">
            <span class="text-sm font-bold leading-none text-on-surface group-hover:text-inherit">{{
              getExchangeDisplayName(contract.exchange)
            }}</span>
            <StrikeOpenInterest
              :conid_yes="contract.conid_yes"
              :conid_no="contract.conid_no"
              :exchange="contract.exchange"
            >
              <template #default="{ formattedValue, isLoading: oiLoading }">
                <span
                  class="flex items-center gap-1 text-[11px] text-muted-color group-hover:text-inherit leading-none"
                >
                  <Skeleton v-if="oiLoading" width="2rem" height="0.6rem" animation="none" />
                  <template v-else>
                    <i class="ibi-arrow-increase-profit text-[9px]" />
                    <span>{{ formattedValue }}</span>
                  </template>
                  <PositionBadge :conids="[contract.conid_yes, contract.conid_no]" :exchanges="[contract.exchange]" />
                </span>
              </template>
            </StrikeOpenInterest>
          </div>
        </div>
        <div class="flex items-center gap-1.5 [&_span]:!text-inherit">
          <i v-if="isBestForSide(contract)" class="ibi-crown text-xs" />
          <i v-if="isCloseForSide(contract)" class="ibi-close-x-filled text-xs" />
          <span v-else>{{ props.side === 'yes' ? 'Y' : 'N' }}</span>
          <BidAskCell
            :conid="props.side === 'yes' ? contract.conid_yes : contract.conid_no"
            :exchange="contract.exchange"
            side="bid"
            format="dollars"
          />
        </div>
      </Button>
    </div>

    <!-- Both-sides mode: exchange rows with separate Yes/No buttons -->
    <div v-else class="min-w-[400px] max-w-[560px] p-4">
      <div
        v-for="contract in contracts"
        :key="contract.exchange"
        class="flex items-center gap-3 py-3 border-b border-(--p-content-border-color) last:border-b-0"
      >
        <!-- Exchange info -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <ExchangeAvatarGroup :exchange="contract.exchange" />
            <div class="min-w-0">
              <p class="text-sm font-semibold truncate">{{ getExchangeDisplayName(contract.exchange) }}</p>
              <StrikeOpenInterest
                :conid_yes="contract.conid_yes"
                :conid_no="contract.conid_no"
                :exchange="contract.exchange"
              >
                <template #default="{ formattedValue, isLoading: oiLoading }">
                  <div class="flex items-center gap-1 text-xs text-muted-color">
                    <Skeleton v-if="oiLoading" width="3rem" height="0.75rem" animation="none" />
                    <template v-else>
                      <i class="ibi-arrow-increase-profit text-[10px]" />
                      <span>{{ formattedValue }}</span>
                    </template>
                    <span class="ml-1"
                      ><PositionBadge
                        :conids="[contract.conid_yes, contract.conid_no]"
                        :exchanges="[contract.exchange]"
                    /></span>
                  </div>
                </template>
              </StrikeOpenInterest>
            </div>
          </div>
        </div>

        <!-- Yes/No buttons -->
        <div class="flex items-center gap-2">
          <Button
            severity="secondary"
            :ib-variant="isYesClose(contract) ? 'close_order' : 'yes'"
            size="small"
            class="btn-exchange !relative !w-[104px] !h-[32px] !overflow-visible !tabular-nums !whitespace-nowrap !font-semibold !text-xs !gap-1 !pl-1 !pr-2 [&_span]:!text-inherit"
            @click="handleYesClick(contract)"
          >
            <i v-if="isYesClose(contract)" class="ibi-close-x-filled text-xs" />
            <span v-else>Y</span>
            <BidAskCell :conid="contract.conid_yes" :exchange="contract.exchange" side="bid" format="dollars" />
            <span
              v-if="yesRating.isBest(contract.conid_yes, contract.exchange)"
              class="crown-chip !absolute !left-0 !top-1/2 !-translate-y-1/2 !-translate-x-1/2 flex items-center justify-center w-6 h-6 rounded-full bg-surface"
            >
              <i
                class="ibi-crown text-[11px]"
                :style="{ color: isYesClose(contract) ? 'var(--p-accent)' : 'var(--p-yes)' }"
              />
            </span>
          </Button>
          <Button
            severity="secondary"
            :ib-variant="isNoClose(contract) ? 'close_order' : 'no'"
            size="small"
            class="btn-exchange !relative !w-[104px] !h-[32px] !overflow-visible !tabular-nums !whitespace-nowrap !font-semibold !text-xs !gap-1 !pl-1 !pr-2 [&_span]:!text-inherit"
            @click="handleNoClick(contract)"
          >
            <i v-if="isNoClose(contract)" class="ibi-close-x-filled text-xs" />
            <span v-else>N</span>
            <BidAskCell :conid="contract.conid_no" :exchange="contract.exchange" side="bid" format="dollars" />
            <span
              v-if="noRating.isBest(contract.conid_no, contract.exchange)"
              class="crown-chip !absolute !right-0 !top-1/2 !-translate-y-1/2 !translate-x-1/2 flex items-center justify-center w-6 h-6 rounded-full bg-surface"
            >
              <i
                class="ibi-crown text-[11px]"
                :style="{ color: isNoClose(contract) ? 'var(--p-accent)' : 'var(--p-no)' }"
              />
            </span>
          </Button>
        </div>
      </div>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Popover from 'primevue/popover';
import Button from 'primevue/button';
import Skeleton from 'primevue/skeleton';
import BidAskCell from '@/markets/BidAskCell.vue';
import StrikeOpenInterest from './StrikeOpenInterest.vue';
import ExchangeAvatarGroup from './ExchangeAvatarGroup.vue';
import PositionBadge from './PositionBadge.vue';
import { useExchangeClose } from '@/composables/useExchangeClose';
import { useEventContractRatingStream } from '@/composables/useEventContractRatingStream';
import { getExchangeDisplayName } from '@/utils/exchangeNames';
import type { PredictionContractListContract } from '@/types/prediction.types';

const { isYesClose, isNoClose, getVariant } = useExchangeClose();

const props = defineProps<{
  contracts: PredictionContractListContract[];
  strikeLabel: string;
  /** When set, only show buttons for the specified side */
  side?: 'yes' | 'no';
  /** When 'close', buttons for contracts with existing same-side positions show as close_order */
  intent?: string;
}>();

const emit = defineEmits<{
  'buy-yes': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
  'buy-no': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
  'close-yes': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
  'close-no': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
}>();

const popoverRef = ref();

// Crown ("best price") driven by the rating service, scoped per side within this strike.
const isMultiVenue = computed(() => props.contracts.length > 1);
const yesPairs = computed(() => (isMultiVenue.value ? props.contracts.map((c) => `${c.conid_yes}@${c.exchange}`) : []));
const noPairs = computed(() => (isMultiVenue.value ? props.contracts.map((c) => `${c.conid_no}@${c.exchange}`) : []));
const yesRating = useEventContractRatingStream(yesPairs, { isLive: () => yesPairs.value.length > 1 });
const noRating = useEventContractRatingStream(noPairs, { isLive: () => noPairs.value.length > 1 });

function sideVariant(contract: PredictionContractListContract): string {
  return getVariant(contract, props.side!, props.intent);
}

function isCloseForSide(contract: PredictionContractListContract): boolean {
  return props.side === 'yes' ? isYesClose(contract, props.intent) : isNoClose(contract, props.intent);
}

function isBestForSide(contract: PredictionContractListContract): boolean {
  return props.side === 'yes'
    ? yesRating.isBest(contract.conid_yes, contract.exchange)
    : noRating.isBest(contract.conid_no, contract.exchange);
}

function handleYesClick(contract: PredictionContractListContract) {
  const eventConidExs = props.contracts.map((c) => `${c.conid_yes}@${c.exchange}`);
  if (isYesClose(contract, props.side ? props.intent : undefined)) {
    emit('close-yes', contract, contract.exchange, eventConidExs);
  } else {
    emit('buy-yes', contract, contract.exchange, eventConidExs);
  }
  popoverRef.value?.hide();
}

function handleNoClick(contract: PredictionContractListContract) {
  const eventConidExs = props.contracts.map((c) => `${c.conid_no}@${c.exchange}`);
  if (isNoClose(contract, props.side ? props.intent : undefined)) {
    emit('close-no', contract, contract.exchange, eventConidExs);
  } else {
    emit('buy-no', contract, contract.exchange, eventConidExs);
  }
  popoverRef.value?.hide();
}

function toggle(event: Event) {
  popoverRef.value?.toggle(event);
}

function hide() {
  popoverRef.value?.hide();
}

defineExpose({ toggle, hide });
</script>
